<script setup lang="ts">
import { asText, isFilled } from '@prismicio/client'
import type { Content } from "@prismicio/client";

const props = defineProps(getSliceComponentProps<Content.BranchGridSlice>());

const { client } = usePrismic()
const { data: branches } = await useAsyncData('branches-grid', async () => {
  const all = await client.getAllByType('branch', {
    orderings: [{ field: 'my.branch.order' }],
  })
  return all.filter((b) => !b.data.hide_in_grid)
})

const hasCtaCard = computed(() =>
  isFilled.link(props.slice.primary.cta_link)
  || isFilled.image(props.slice.primary.cta_image)
  || !!props.slice.primary.cta_title
  || !!props.slice.primary.cta_description,
)

const branchesBefore = computed(() => branches.value?.slice(0, 2) ?? [])
const branchesAfter = computed(() => branches.value?.slice(2) ?? [])
</script>

<template>
  <section
      :data-slice-type="slice.slice_type"
      :data-slice-variation="slice.variation"
      :class="['pa-branch-grid', 'pa-branch-grid--' + slice.variation]"
  >
    <div class="pa-container">
      <div v-if="slice.primary.eyebrow || slice.primary.heading" class="pa-branch-grid__header">
        <div v-if="slice.primary.eyebrow" class="pa-branch-grid__eyebrow">
          {{ slice.primary.eyebrow }}
        </div>
        <div class="pa-branch-grid__heading">
          <PrismicRichText :field="slice.primary.heading" />
        </div>
      </div>

      <div class="pa-branch-grid__grid">
        <NuxtLink
            v-for="branch in branchesBefore"
            :key="branch.id"
            :to="branch.url || `/sectoren/${branch.uid}`"
            class="pa-branch-grid__card"
        >
          <div class="pa-branch-grid__card-content">
            <h3 class="pa-branch-grid__card-title">
              {{ asText(branch.data.title) }}
            </h3>
            <hr class="pa-branch-grid__card-divider" />
            <span class="pa-branch-grid__card-arrow" aria-hidden="true">&rarr;</span>
          </div>
          <div class="pa-branch-grid__card-image">
            <PrismicImage
                v-if="branch.data.featured_image?.url"
                :field="branch.data.featured_image"
                :widths="[400, 600, 800, 1200]"
                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
            />
          </div>
        </NuxtLink>

        <div v-if="hasCtaCard" class="pa-branch-grid__card pa-branch-grid__card--cta">
          <div class="pa-branch-grid__card-content">
            <h3 v-if="slice.primary.cta_title" class="pa-branch-grid__card-cta-title">
              {{ slice.primary.cta_title }}
            </h3>
            <p v-if="slice.primary.cta_description" class="pa-branch-grid__card-cta-description">
              {{ slice.primary.cta_description }}
            </p>
            <TrackedPrismicLink
                v-if="isFilled.link(slice.primary.cta_link)"
                :field="slice.primary.cta_link"
                :track="slice.primary.cta_track_event"
                class="pa-btn pa-btn--white pa-branch-grid__card-cta-link"
            >
              {{ slice.primary.cta_link.text || 'Neem contact op' }}
            </TrackedPrismicLink>
          </div>
          <div v-if="slice.primary.cta_image?.url" class="pa-branch-grid__card-image">
            <PrismicImage
                :field="slice.primary.cta_image"
                :widths="[400, 600, 800, 1200]"
                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
            />
          </div>
        </div>

        <NuxtLink
            v-for="branch in branchesAfter"
            :key="branch.id"
            :to="branch.url || `/sectoren/${branch.uid}`"
            class="pa-branch-grid__card"
        >
          <div class="pa-branch-grid__card-content">
            <h3 class="pa-branch-grid__card-title">
              {{ asText(branch.data.title) }}
            </h3>
            <hr class="pa-branch-grid__card-divider" />
            <span class="pa-branch-grid__card-arrow" aria-hidden="true">&rarr;</span>
          </div>
          <div class="pa-branch-grid__card-image">
            <PrismicImage
                v-if="branch.data.featured_image?.url"
                :field="branch.data.featured_image"
                :widths="[400, 600, 800, 1200]"
                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
            />
          </div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
